import React from 'react';
import { Card, List, Tag, Typography, Divider, Button, message } from 'antd';
import { AIAnalysisResult } from '../../Questionnaire/NewAI/shared/types';
import {
  formatEndoteleonName,
  formatBiochemicalName,
  getUncertaintyTagColor,
  getDirectionColor,
  getAmplitudeColor,
  getCongruenceColor,
  t
} from './renderingHelpers';
import { useGenerateSummaryReport } from '../../Questionnaire/NewAI/shared/hooks';

const { Title, Paragraph, Text } = Typography;

interface AIAnalysisResultPrompt2Props {
  result: AIAnalysisResult;
  lang: 'en' | 'nl';
  clientName?: string;
  professionalName?: string;
}

const AIAnalysisResultPrompt2: React.FC<AIAnalysisResultPrompt2Props> = ({ 
  result, 
  lang, 
  clientName, 
  professionalName 
}) => {
  const { generateSummaryReport, loading } = useGenerateSummaryReport();

  const buildSummaryPayload = () => {
    const { ["client-name"]: _client, ["professional-name"]: _prof, ...rest } = result;
    
    return {
      prompt_id: "Prompt_2",
      "client-name": clientName || '',
      "professional-name": professionalName || '',
      ...rest
    };
  };


  const handleViewSummaryReport = async () => {
    try {
      await generateSummaryReport(buildSummaryPayload());
      message.success(t(lang, 'summaryReportDownloaded'));
    } catch (err: any) {
      message.error(err?.message || 'Error generating summary report.');
    }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button type="primary" onClick={handleViewSummaryReport} loading={loading}>
          {t(lang, 'generateSummaryReport') || 'Generate Summary Report'}
        </Button>
      </div>

      {/* A1 Section - Primary and Secondary Analysis */}
      <div style={{ marginBottom: 24 }}>
        <Title level={5}>{t(lang, 'a1Title')}</Title>
        <Card size="small" style={{ marginBottom: 16 }}>
          <div>
            <Text strong>{t(lang, 'primary')}</Text>
            <div style={{ marginTop: 8, marginBottom: 16 }}>
              <Tag color="blue" style={{ fontSize: '14px', padding: '4px 8px' }}>
                {formatEndoteleonName(result.A1.primary_ie)}
              </Tag>
            </div>
            
            <Text strong>{t(lang, 'secondary')}</Text>
            <div style={{ marginTop: 8, marginBottom: 16 }}>
              {result.A1.secondary_ie.map((item: string, index: number) => (
                <Tag key={index} color="green" style={{ fontSize: '14px', padding: '4px 8px', marginBottom: '4px' }}>
                  {formatEndoteleonName(item)}
                </Tag>
              ))}
            </div>

            {result.A1.secondary_dynamics && result.A1.secondary_dynamics.length > 0 && (
              <>
                <Text strong>{t(lang, 'secondaryDynamics')}</Text>
                <div style={{ marginTop: 8, marginBottom: 16 }}>
                  {result.A1.secondary_dynamics.map((dynamic: string, index: number) => (
                    <Paragraph key={index} style={{ marginTop: 4, marginBottom: 4 }}>
                      • {dynamic}
                    </Paragraph>
                  ))}
                </div>
              </>
            )}
            
            <Text strong>{t(lang, 'teleonic')}</Text>
            <Paragraph style={{ marginTop: 8, backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '6px' }}>
              {result.A1.teleonic_pattern}
            </Paragraph>

            <Text strong>{t(lang, 'uncertainty')}</Text>
            <div style={{ marginTop: 8 }}>
              <Tag color={getUncertaintyTagColor(result.A1.uncertainty_level)}>
                {result.A1.uncertainty_label}
              </Tag>
            </div>
          </div>
        </Card>
      </div>

      {/* A3 Section - Biochemical Markers */}
      <div style={{ marginBottom: 24 }}>
        <Title level={5}>{t(lang, 'a3Title')}</Title>
        <Card size="small">
          <div style={{ marginBottom: 16 }}>
            <Text strong>{t(lang, 'directMarkers')}</Text>
            <div style={{ marginTop: 8 }}>
              {result.A3.direct.map((marker: string, index: number) => (
                <Tag key={index} color="orange" style={{ fontSize: '14px', padding: '4px 8px', marginRight: '8px', marginBottom: '4px' }}>
                  {formatBiochemicalName(marker)}
                </Tag>
              ))}
            </div>
          </div>
          <div>
            <Text strong>{t(lang, 'indirectMarkers')}</Text>
            <div style={{ marginTop: 8 }}>
              {result.A3.indirect.map((marker: string, index: number) => (
                <Tag key={index} color="cyan" style={{ fontSize: '14px', padding: '4px 8px', marginRight: '8px', marginBottom: '4px' }}>
                  {formatBiochemicalName(marker)}
                </Tag>
              ))}
            </div>
          </div>
        </Card>
      </div>

      {/* A4a Section - Questionnaire Module Analysis */}
      {result.A4a && result.A4a.status === 'present' && result.A4a.items && result.A4a.items.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <Title level={5}>{t(lang, 'a4aTitle')}</Title>
          <Card size="small">
            {result.A4a.items.map((item, index) => (
              <div 
                key={index} 
                style={{ 
                  marginBottom: index < result.A4a!.items.length - 1 ? 24 : 0, 
                  paddingBottom: index < result.A4a!.items.length - 1 ? 24 : 0, 
                  borderBottom: index < result.A4a!.items.length - 1 ? '1px solid #f0f0f0' : 'none' 
                }}
              >
                <Title level={5} style={{ marginBottom: 12, color: '#1890ff' }}>
                  {item.qest_json_identifier.toUpperCase()}
                </Title>
                
                <div style={{ marginBottom: 12 }}>
                  <Text strong>{t(lang, 'teleonicField')}</Text>
                  <Paragraph style={{ marginTop: 4 }}>{item.teleonic_field}</Paragraph>
                </div>

                <div style={{ marginBottom: 12, display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <div>
                    <Text strong style={{ marginRight: 4 }}>{t(lang, 'direction')}</Text>
                    <Tag color={getDirectionColor(item.direction)}>
                      {item.direction}
                    </Tag>
                  </div>
                  <div>
                    <Text strong style={{ marginRight: 4 }}>{t(lang, 'amplitude')}</Text>
                    <Tag color={getAmplitudeColor(item.amplitude)}>
                      {item.amplitude}
                    </Tag>
                  </div>
                  <div>
                    <Text strong style={{ marginRight: 4 }}>{t(lang, 'congruence')}</Text>
                    <Tag color={getCongruenceColor(item.congruence)}>
                      {item.congruence}
                    </Tag>
                  </div>
                </div>

                {item.heta_context && item.heta_context.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <Text strong>{t(lang, 'hetaContext')}</Text>
                    <div style={{ marginTop: 4 }}>
                      {item.heta_context.map((heta: string, idx: number) => (
                        <Tag key={idx} color="gold" style={{ marginRight: '4px', marginBottom: '4px' }}>
                          {heta}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: 12 }}>
                  <Text strong>{t(lang, 'narrative')}</Text>
                  <List
                    size="small"
                    dataSource={item.narrative}
                    renderItem={(narr, idx) => (
                      <List.Item key={idx} style={{ paddingLeft: 0, paddingRight: 0, border: 'none' }}>
                        <Text style={{ fontSize: '13px' }}>• {narr}</Text>
                      </List.Item>
                    )}
                  />
                </div>
              </div>
            ))}
          </Card>
        </div>
      )}

      {/* A4b Section - Hair Mineral Analysis */}
      {result.A4b && result.A4b.status === 'present' && (
        <div style={{ marginBottom: 24 }}>
          <Title level={5}>{t(lang, 'a4bTitle')}</Title>
          <Card size="small">
            <div style={{ marginBottom: 16 }}>
              <Text strong>{t(lang, 'eapProfile')}</Text>
              <Tag color="blue" style={{ marginLeft: 8, fontSize: '14px', padding: '4px 8px' }}>
                {result.A4b.summary_components.eap_profile}
              </Tag>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong>{t(lang, 'eapIndex')}</Text>
              <Tag color="purple" style={{ marginLeft: 8, fontSize: '14px', padding: '4px 8px' }}>
                {result.A4b.summary_components.eap_index}
              </Tag>
            </div>

            <div style={{ marginBottom: 16, display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <div>
                <Text strong>{t(lang, 'intensity')}</Text>
                <Tag color={result.A4b.summary_components.intensity > 2 ? 'red' : 'orange'} style={{ marginLeft: 8 }}>
                  {result.A4b.summary_components.intensity}
                </Tag>
              </div>
              <div>
                <Text strong>{t(lang, 'direction')}</Text>
                <Tag color="blue" style={{ marginLeft: 8 }}>
                  {result.A4b.summary_components.direction}
                </Tag>
              </div>
              <div>
                <Text strong>{t(lang, 'buffer')}</Text>
                <Tag color="cyan" style={{ marginLeft: 8 }}>
                  {result.A4b.summary_components.buffer}
                </Tag>
              </div>
            </div>

            {result.A4b.summary_components.pattern_markers && result.A4b.summary_components.pattern_markers.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>{t(lang, 'patternMarkers')}</Text>
                <div style={{ marginTop: 8 }}>
                  {result.A4b.summary_components.pattern_markers.map((marker: string, idx: number) => (
                    <Tag key={idx} color="green" style={{ marginRight: '4px', marginBottom: '4px' }}>
                      {marker}
                    </Tag>
                  ))}
                </div>
              </div>
            )}

            <Divider style={{ margin: '16px 0' }} />

            <div style={{ marginBottom: 12 }}>
              <Text strong>{t(lang, 'epistemicPositioning')}</Text>
              <Paragraph style={{ marginTop: 4 }}>{result.A4b.sections.epistemic_positioning}</Paragraph>
            </div>

            <div style={{ marginBottom: 12 }}>
              <Text strong>{t(lang, 'overallPattern')}</Text>
              <Paragraph style={{ marginTop: 4 }}>{result.A4b.sections.overall_pattern}</Paragraph>
            </div>

            <div>
              <Text strong>{t(lang, 'summary')}</Text>
              <Paragraph style={{ marginTop: 8, backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '6px' }}>
                {result.A4b.sections.summary}
              </Paragraph>
            </div>
          </Card>
        </div>
      )}

      {/* A4c Section - Heart Rate Variability */}
      {result.A4c && result.A4c.status === 'present' && (
        <div style={{ marginBottom: 24 }}>
          <Title level={5}>{t(lang, 'a4cTitle')}</Title>
          <Card size="small">
            <div style={{ marginBottom: 16 }}>
              <Text strong>{t(lang, 'rapProfile')}</Text>
              <Paragraph style={{ marginTop: 4 }}>{result.A4c.sections.rap_profile}</Paragraph>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong>{t(lang, 'rapIndex')}</Text>
              <Tag color="blue" style={{ marginLeft: 8, fontSize: '14px', padding: '4px 8px' }}>
                {result.A4c.sections.rap_index}
              </Tag>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            <div style={{ marginBottom: 12 }}>
              <Text strong>{t(lang, 'measurementContext')}</Text>
              <Paragraph style={{ marginTop: 4 }}>{result.A4c.sections.measurement_context}</Paragraph>
            </div>

            <div style={{ marginBottom: 12 }}>
              <Text strong>{t(lang, 'globalLoadRecovery')}</Text>
              <Paragraph style={{ marginTop: 4, backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '6px' }}>
                {result.A4c.sections.global_load_recovery}
              </Paragraph>
            </div>

            <div style={{ marginBottom: 12 }}>
              <Text strong>{t(lang, 'nightRecovery')}</Text>
              <Paragraph style={{ marginTop: 4 }}>{result.A4c.sections.night_recovery_sleep_rhythm}</Paragraph>
            </div>

            <div>
              <Text strong>{t(lang, 'activityDistribution')}</Text>
              <Paragraph style={{ marginTop: 4 }}>{result.A4c.sections.activity_day_distribution}</Paragraph>
            </div>
          </Card>
        </div>
      )}

      {/* A5 Section - Pre-intake Analysis */}
      {result.A5 && result.A5.items && result.A5.items.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <Title level={5}>{t(lang, 'a5Title')}</Title>
          <Card size="small">
            <List
              dataSource={result.A5.items}
              renderItem={(item, index) => (
                <List.Item key={index}>
                  <div style={{ width: '100%' }}>
                    {item.item_type.toLowerCase().includes('red_flag') ? (
                      <>
                        <Tag color="red" style={{ marginBottom: 8 }}>{t(lang, 'redFlag')}</Tag>
                        <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>{item.text}</Paragraph>
                      </>
                    ) : (
                      <>
                        <Tag color="blue" style={{ marginBottom: 8 }}>{t(lang, 'additionalLabAnalysis')}</Tag>
                        <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                          {item.lab_type && <Text strong>{item.lab_type.toUpperCase()}: </Text>}
                          {item.text}
                        </Paragraph>
                      </>
                    )}
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </div>
      )}
    </>
  );
};

export default AIAnalysisResultPrompt2;